import { useState, useEffect, useCallback } from "react";
import { useParams } from "react-router-dom";
import { T, F, Wordmark } from "@/lib/bricks";
import VisitViewer from "@/components/VisitViewer";
import { visitFromSharePayload } from "@/lib/visitBridge";
import { partitionSharePhotos } from "@/lib/mediaKind";
import BricksLoader from "@/components/BricksLoader";

function tourEmbedSrc(url) {
  if (!url) return null;
  if (/^https?:\/\//i.test(url)) return url;
  const base = (process.env.REACT_APP_BACKEND_URL || "").replace(/\/$/, "");
  return `${base}${url.startsWith("/") ? url : `/${url}`}`;
}

function photoFullSrc(ph) {
  return ph?.download_url || ph?.file_url || ph?.url || ph?.thumbnail_url || "";
}

export default function ClientSharePage() {
  const { token } = useParams();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [lightboxIndex, setLightboxIndex] = useState(null);
  const [floorplanOpen, setFloorplanOpen] = useState(false);
  const [downloading, setDownloading] = useState(false);
  const [zipError, setZipError] = useState(null);
  const [zipPreset, setZipPreset] = useState("original");
  const [linkCopied, setLinkCopied] = useState(false);
  // Phone: stack the address over the actions instead of squeezing it.
  const [narrow, setNarrow] = useState(() => typeof window !== "undefined" && window.innerWidth < 640);
  useEffect(() => {
    const onResize = () => setNarrow(window.innerWidth < 640);
    window.addEventListener("resize", onResize);
    return () => window.removeEventListener("resize", onResize);
  }, []);

  const BASE = process.env.REACT_APP_BACKEND_URL;
  // Honest sizes: photos are delivered as 2400px edits today (no larger master
  // is kept yet), so there is no "full resolution" or "print" choice to offer.
  const ZIP_PRESETS = [
    { id: "original", label: "As delivered (2400px)" },
    { id: "mls", label: "MLS (2400px)" },
    { id: "web", label: "Web (1920px)" },
  ];
  const PRESET_HINT = {
    original: "Edited photos at 2400px. Video and plans as delivered.",
    web: "1920px long edge. Lighter files for web and social.",
    mls: "4:3 crop, 2400px long edge.",
  };

  const copyPageLink = useCallback(async () => {
    try {
      await navigator.clipboard.writeText(window.location.href);
      setLinkCopied(true);
      window.setTimeout(() => setLinkCopied(false), 2000);
    } catch {
      setLinkCopied(false);
    }
  }, []);

  useEffect(() => {
    fetch(`${BASE}/api/public/share/${encodeURIComponent(token)}`)
      .then(r => {
        if (!r.ok) throw new Error(r.status === 404 ? "This share link is invalid or has expired." : "Failed to load media.");
        return r.json();
      })
      .then(d => { setData(d); setLoading(false); })
      .catch(err => { setError(err.message || "Failed to load."); setLoading(false); });
  }, [token]); // eslint-disable-line

  const photos = data?.photos || [];
  const { stillPhotos, dronePhotos, reelPhotos } = partitionSharePhotos(photos);
  const lightboxOpen = lightboxIndex != null && lightboxIndex >= 0 && lightboxIndex < photos.length;
  const activePhoto = lightboxOpen ? photos[lightboxIndex] : null;
  const activeSrc = photoFullSrc(activePhoto);

  const closeLightbox = useCallback(() => setLightboxIndex(null), []);
  const showPrev = useCallback(() => {
    setLightboxIndex((i) => (i == null || photos.length === 0 ? i : (i - 1 + photos.length) % photos.length));
  }, [photos.length]);
  const showNext = useCallback(() => {
    setLightboxIndex((i) => (i == null || photos.length === 0 ? i : (i + 1) % photos.length));
  }, [photos.length]);

  const closeFloorplan = useCallback(() => setFloorplanOpen(false), []);

  const handleDownloadAll = useCallback(async () => {
    if (downloading || !token) return;
    setZipError(null);
    setDownloading(true);
    try {
      const q = zipPreset && zipPreset !== "original" ? `?preset=${encodeURIComponent(zipPreset)}` : "?preset=original";
      const res = await fetch(`${BASE}/api/public/share/${encodeURIComponent(token)}/media.zip${q}`);
      if (!res.ok) {
        let detail = res.status === 404 ? "No downloadable files available yet." : "Failed to prepare zip";
        try {
          const body = await res.json();
          if (typeof body?.detail === "string") detail = body.detail;
        } catch { /* keep */ }
        throw new Error(detail);
      }
      const blob = await res.blob();
      if (blob.type && blob.type.includes("application/json")) {
        const parsed = JSON.parse(await blob.text());
        throw new Error(typeof parsed?.detail === "string" ? parsed.detail : "Failed to prepare zip");
      }
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      const cd = res.headers.get("content-disposition") || "";
      const match = /filename="?([^";]+)"?/i.exec(cd);
      a.download = match?.[1] || "listing-media.zip";
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
    } catch (err) {
      setZipError(err?.message || "Failed to prepare zip");
    } finally {
      setDownloading(false);
    }
  }, [BASE, downloading, token, zipPreset]);

  useEffect(() => {
    if (!lightboxOpen && !floorplanOpen) return undefined;
    const onKey = (e) => {
      if (e.key === "Escape") {
        if (lightboxOpen) closeLightbox();
        if (floorplanOpen) closeFloorplan();
      }
      if (lightboxOpen && e.key === "ArrowLeft") showPrev();
      if (lightboxOpen && e.key === "ArrowRight") showNext();
    };
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    window.addEventListener("keydown", onKey);
    return () => {
      document.body.style.overflow = prevOverflow;
      window.removeEventListener("keydown", onKey);
    };
  }, [lightboxOpen, floorplanOpen, closeLightbox, closeFloorplan, showPrev, showNext]);

  const pageStyle = {
    minHeight: "100vh", background: "#F4F1EB", fontFamily: F.display,
    padding: "40px 16px 64px", color: "#14110E",
  };

  if (loading) {
    return (
      <div style={pageStyle}>
        <div style={{ textAlign: "center", paddingTop: 80 }}>
          <BricksLoader variant="assemble" size={40} color={T.brick} style={{ display: 'block', margin: '0 auto' }} />
          <div style={{ marginTop: 16, fontSize: 14, color: "#6B6054" }}>Loading media…</div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div style={pageStyle}>
        <div style={{ maxWidth: 480, margin: "96px auto", padding: "0 8px", textAlign: "center" }}>
          <div style={{ fontSize: 20, fontWeight: 500, letterSpacing: -0.5 }}>Link unavailable</div>
          <div style={{ fontSize: 14, color: "#6B6054", marginTop: 8 }}>{error}</div>
        </div>
      </div>
    );
  }

  const tourSrc = tourEmbedSrc(data.tour_embed_url);
  const empty = data.empty;
  const amenityLabels = Array.isArray(data.amenity_labels)
    ? [...new Set(data.amenity_labels.map((l) => String(l || "").trim()).filter(Boolean))]
    : [];

  return (
    <div data-testid="client-share-page" style={pageStyle}>
      <div style={{ maxWidth: 920, margin: "0 auto" }}>
        <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 28 }}>
          <Wordmark size={18} />
          <span style={{ fontSize: 12, color: "#A39684" }}>
            Client media
          </span>
        </div>

        <div style={{ display: "flex", alignItems: "flex-start", justifyContent: "space-between", gap: 16, flexWrap: "wrap" }}>
          <div style={{ minWidth: 0, flex: narrow ? "1 1 100%" : "1 1 280px" }}>
            <div style={{ fontSize: narrow ? 24 : 28, fontWeight: 500, letterSpacing: -0.8, lineHeight: 1.15 }}>{data.address || "Listing media"}</div>
            <div style={{ fontSize: 13, color: "#6B6054", marginTop: 6 }}>
              {[tourSrc && "Tour", visitFromSharePayload(data) && "Visit", stillPhotos.length > 0 && "Photos", dronePhotos.length > 0 && "Drone", reelPhotos.length > 0 && "Video", data.floorplan_url && "Floor plan"].filter(Boolean).join(" · ") || "Listing media"} · shared via bricks
            </div>
          </div>
          <div style={{ display: "flex", flexDirection: "column", alignItems: narrow ? "stretch" : "flex-end", gap: 8, width: narrow ? "100%" : undefined }}>
            <button
              type="button"
              data-testid="client-share-copy-link"
              onClick={copyPageLink}
              style={{
                fontFamily: F.display, fontSize: 13, padding: "0", minHeight: 44, border: "none", background: "none",
                color: "#3B332A", textDecoration: "underline", textUnderlineOffset: 3, textDecorationColor: "#14111033",
                cursor: "pointer", whiteSpace: "nowrap", alignSelf: narrow ? "flex-start" : "flex-end",
              }}
            >
              {linkCopied ? "Link copied" : "Copy this link"}
            </button>
            {!empty && (photos.length > 0 || data.floorplan_url) && (
              <div style={{ display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap", justifyContent: narrow ? "space-between" : "flex-end" }}>
                <label style={{ display: "flex", alignItems: "center", gap: 6, fontSize: 12, color: "#6B6054", flex: narrow ? "1 1 auto" : undefined }}>
                  <span style={{ fontSize: 12 }}>Size</span>
                  <select
                    data-testid="client-share-zip-preset"
                    value={zipPreset}
                    onChange={(e) => setZipPreset(e.target.value)}
                    disabled={downloading}
                    style={{
                      fontFamily: F.display, fontSize: 13,
                      padding: "0 10px", minHeight: 44, borderRadius: 2,
                      border: "1px solid #14111018", background: "#EDE8DF", color: "#14110E",
                      flex: narrow ? "1 1 auto" : undefined, minWidth: 0,
                    }}
                  >
                    {ZIP_PRESETS.map((p) => (
                      <option key={p.id} value={p.id}>{p.label}</option>
                    ))}
                  </select>
                </label>
                <button
                  type="button"
                  data-testid="client-share-download-all"
                  onClick={handleDownloadAll}
                  disabled={downloading}
                  style={{
                    fontFamily: F.display, fontSize: 13, fontWeight: 500,
                    padding: "0 16px", minHeight: 44, borderRadius: 2, border: "none",
                    background: downloading ? "#A39684" : "#14110E", color: "#F4F1EB",
                    cursor: downloading ? "default" : "pointer",
                    whiteSpace: "nowrap",
                  }}
                >
                  {downloading ? "Preparing zip…" : "Download all"}
                </button>
              </div>
            )}
            {!empty && (photos.length > 0 || data.floorplan_url) && (
              <div style={{ fontSize: 11, color: "#A39684", maxWidth: narrow ? "none" : 260, textAlign: narrow ? "left" : "right" }}>
                {PRESET_HINT[zipPreset] || PRESET_HINT.original}
              </div>
            )}
          </div>
        </div>
        {zipError && (
          <div style={{ marginTop: 10, fontSize: 13, color: "#8C2F24" }}>{zipError}</div>
        )}

        {empty ? (
          <div style={{ marginTop: 48, padding: "48px var(--bx-pad)", textAlign: "center", border: "1px dashed #14111020", borderRadius: 2, color: "#6B6054", fontSize: 14 }}>
            Nothing delivered yet. Check back once media is ready.
          </div>
        ) : (
          <div style={{ display: "flex", flexDirection: "column", gap: 24, marginTop: 32 }}>
            {tourSrc && (
              <div>
                <div style={{ fontSize: 12, color: "#A39684", marginBottom: 10 }}>3D tour</div>
                <iframe
                  src={tourSrc}
                  title="3D tour"
                  allow="fullscreen; xr-spatial-tracking"
                  style={{ width: "100%", height: 420, border: 0, borderRadius: 2, background: "#0a0a0a", display: "block" }}
                />
              </div>
            )}


            {visitFromSharePayload(data) && (
              <div data-testid="client-share-visit">
                <div style={{ fontSize: 12, color: "#A39684", marginBottom: 10 }}>Virtual visit</div>
                <VisitViewer visit={visitFromSharePayload(data)} />
              </div>
            )}

            {amenityLabels.length > 0 && (
              <div data-testid="client-share-amenities">
                <div style={{ fontSize: 12, color: "#A39684", marginBottom: 10 }}>
                  Features & amenities
                </div>
                <div style={{ fontSize: 14, color: "#3B332A", lineHeight: 1.7 }}>{amenityLabels.join(" · ")}</div>
              </div>
            )}

            {data.floorplan_url && (
              <div data-testid="client-share-floorplan">
                <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 12, marginBottom: 10, flexWrap: "wrap" }}>
                  <div style={{ fontSize: 12, color: "#A39684" }}>Floor plan</div>
                  <a
                    href={data.floorplan_url}
                    download="floor-plan"
                    rel="noopener noreferrer"
                    target="_blank"
                    style={{
                      fontFamily: F.display, fontSize: 13, color: "#3B332A",
                      textDecoration: "underline", textUnderlineOffset: 3, textDecorationColor: "#14111033",
                    }}
                  >
                    Download
                  </a>
                </div>
                <div
                  role="button"
                  tabIndex={0}
                  aria-label="Enlarge floor plan"
                  onClick={() => setFloorplanOpen(true)}
                  onKeyDown={(e) => { if (e.key === "Enter" || e.key === " ") { e.preventDefault(); setFloorplanOpen(true); } }}
                  style={{ position: "relative", cursor: "pointer", borderRadius: 2, overflow: "hidden", background: "#EDE8DF" }}
                >
                  <img
                    src={data.floorplan_url}
                    alt="Floor plan"
                    style={{ width: "100%", maxHeight: 480, objectFit: "contain", borderRadius: 2, background: "#EDE8DF", display: "block" }}
                  />
                  <span
                    style={{
                      position: "absolute", bottom: 8, right: 8,
                      fontSize: 12, 
                      padding: "4px 8px", borderRadius: 2,
                      background: "rgba(20,17,14,0.55)", color: "#F4F1EB",
                    }}
                  >
                    Enlarge
                  </span>
                </div>
              </div>
            )}

            {[
              { key: "photos", label: "Photos", items: stillPhotos },
              { key: "drone", label: "Drone", items: dronePhotos },
              { key: "reel", label: "Video", items: reelPhotos },
            ].map((section) => (
              section.items.length > 0 ? (
                <div key={section.key}>
                  <div style={{ fontSize: 12, color: "#A39684", marginBottom: 10 }}>
                    {section.label} · {section.items.length}
                  </div>
                  <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(140px, 1fr))", gap: 10 }}>
                    {section.items.map((ph) => {
                      const i = photos.indexOf(ph);
                      const full = photoFullSrc(ph);
                      return (
                        <div
                          key={ph.id || `${section.key}-${i}`}
                          data-testid="client-share-photo"
                          style={{ position: "relative", borderRadius: 2, overflow: "hidden", cursor: "pointer", background: "#EDE8DF" }}
                          onClick={() => setLightboxIndex(i)}
                          onKeyDown={(e) => { if (e.key === "Enter" || e.key === " ") { e.preventDefault(); setLightboxIndex(i); } }}
                          role="button"
                          tabIndex={0}
                          aria-label={`Open ${section.label.toLowerCase()} ${i + 1}`}
                        >
                          {ph.stream_url ? (
                            <>
                              <video
                                data-testid="client-share-video-thumb"
                                src={`${ph.stream_url}#t=0.1`}
                                preload="metadata"
                                muted
                                playsInline
                                style={{ width: "100%", height: 140, objectFit: "cover", borderRadius: 2, display: "block", background: "#14110E", pointerEvents: "none" }}
                              />
                              <span
                                aria-hidden="true"
                                style={{
                                  position: "absolute", top: "50%", left: "50%", transform: "translate(-50%, -50%)",
                                  width: 40, height: 40, borderRadius: 20, background: "rgba(20,17,14,0.55)",
                                  display: "flex", alignItems: "center", justifyContent: "center", color: "#F4F1EB", fontSize: 14,
                                }}
                              >
                                ▶
                              </span>
                            </>
                          ) : (
                            <img
                              src={ph.thumbnail_url || full}
                              alt={ph.filename || `${section.label} ${i + 1}`}
                              style={{ width: "100%", height: 140, objectFit: "cover", borderRadius: 2, display: "block" }}
                            />
                          )}
                          {full && (
                            <a
                              href={full}
                              download={ph.filename || undefined}
                              rel="noopener noreferrer"
                              target="_blank"
                              onClick={(e) => e.stopPropagation()}
                              aria-label={`Download ${ph.filename || `${section.label} ${i + 1}`}`}
                              style={{
                                position: "absolute", bottom: 8, right: 8,
                                width: 44, height: 44,
                                background: "rgba(20,17,14,0.55)",
                                borderRadius: 2,
                                display: "flex", alignItems: "center", justifyContent: "center",
                                textDecoration: "none", color: "#F4F1EB",
                                fontFamily: F.mono, fontSize: 11, fontWeight: 600,
                              }}
                              title="Download"
                            >
                              ↓
                            </a>
                          )}
                        </div>
                      );
                    })}
                  </div>
                </div>
              ) : null
            ))}
          </div>
        )}
      </div>

      {lightboxOpen && activePhoto && (
        <div
          data-testid="client-share-lightbox"
          role="dialog"
          aria-modal="true"
          aria-label={activePhoto.filename || `Photo ${lightboxIndex + 1}`}
          onClick={closeLightbox}
          style={{
            position: "fixed", inset: 0, zIndex: 1000,
            background: "rgba(20,17,14,0.88)",
            display: "flex", alignItems: "center", justifyContent: "center",
            padding: 'var(--bx-pad)',
          }}
        >
          <div
            onClick={(e) => e.stopPropagation()}
            style={{
              position: "relative",
              maxWidth: "min(1100px, 100%)",
              maxHeight: "100%",
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              gap: 12,
            }}
          >
            {activePhoto.stream_url ? (
              <video
                key={activePhoto.stream_url}
                data-testid="client-share-video"
                src={activePhoto.stream_url}
                controls
                autoPlay
                playsInline
                preload="metadata"
                style={{
                  maxWidth: "100%",
                  width: "min(1100px, 100vw - 32px)",
                  maxHeight: "calc(100vh - 120px)",
                  borderRadius: 2,
                  display: "block",
                  background: "#14110E",
                }}
              />
            ) : (
            <img
              src={activeSrc}
              alt={activePhoto.filename || `Photo ${lightboxIndex + 1}`}
              style={{
                maxWidth: "100%",
                maxHeight: "calc(100vh - 120px)",
                objectFit: "contain",
                borderRadius: 2,
                display: "block",
                background: "#14110E",
              }}
            />
            )}
            <div style={{ display: "flex", alignItems: "center", gap: 10, flexWrap: "wrap", justifyContent: "center" }}>
              {photos.length > 1 && (
                <button
                  type="button"
                  onClick={showPrev}
                  aria-label="Previous photo"
                  style={{
                    fontFamily: F.display, fontSize: 13, fontWeight: 500,
                    padding: "8px 12px", borderRadius: 2, border: "1px solid #F4F1EB26",
                    background: "#1C1A17", color: "#F4F1EB", cursor: "pointer",
                  }}
                >
                  Prev
                </button>
              )}
              <span style={{ fontFamily: F.mono, fontSize: 12, color: "#A39684" }}>
                {lightboxIndex + 1} / {photos.length}
              </span>
              {photos.length > 1 && (
                <button
                  type="button"
                  onClick={showNext}
                  aria-label="Next photo"
                  style={{
                    fontFamily: F.display, fontSize: 13, fontWeight: 500,
                    padding: "8px 12px", borderRadius: 2, border: "1px solid #F4F1EB26",
                    background: "#1C1A17", color: "#F4F1EB", cursor: "pointer",
                  }}
                >
                  Next
                </button>
              )}
              {activeSrc && (
                <a
                  href={activeSrc}
                  download={activePhoto.filename || undefined}
                  rel="noopener noreferrer"
                  target="_blank"
                  style={{
                    fontFamily: F.display, fontSize: 13, fontWeight: 500,
                    padding: "8px 14px", borderRadius: 2, border: "none",
                    background: "#F4F1EB", color: "#14110E", textDecoration: "none",
                  }}
                >
                  Download
                </a>
              )}
              <button
                type="button"
                onClick={closeLightbox}
                aria-label="Close"
                style={{
                  fontFamily: F.display, fontSize: 13, fontWeight: 500,
                  padding: "8px 12px", borderRadius: 2, border: "1px solid #F4F1EB26",
                  background: "transparent", color: "#D6CFC2", cursor: "pointer",
                }}
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}

      {floorplanOpen && data?.floorplan_url && (
        <div
          data-testid="client-share-floorplan-lightbox"
          role="dialog"
          aria-modal="true"
          aria-label="Floor plan"
          onClick={closeFloorplan}
          style={{
            position: "fixed", inset: 0, zIndex: 1000,
            background: "rgba(20,17,14,0.88)",
            display: "flex", alignItems: "center", justifyContent: "center",
            padding: 'var(--bx-pad)',
          }}
        >
          <div
            onClick={(e) => e.stopPropagation()}
            style={{
              position: "relative",
              maxWidth: "min(1100px, 100%)",
              maxHeight: "100%",
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              gap: 12,
            }}
          >
            <img
              src={data.floorplan_url}
              alt="Floor plan"
              style={{
                maxWidth: "100%",
                maxHeight: "calc(100vh - 120px)",
                objectFit: "contain",
                borderRadius: 2,
                display: "block",
                background: "#14110E",
              }}
            />
            <div style={{ display: "flex", alignItems: "center", gap: 10, flexWrap: "wrap", justifyContent: "center" }}>
              <a
                href={data.floorplan_url}
                download="floor-plan"
                rel="noopener noreferrer"
                target="_blank"
                style={{
                  fontFamily: F.display, fontSize: 13, fontWeight: 500,
                  padding: "8px 14px", borderRadius: 2, border: "none",
                  background: "#F4F1EB", color: "#14110E", textDecoration: "none",
                }}
              >
                Download
              </a>
              <button
                type="button"
                onClick={closeFloorplan}
                aria-label="Close floor plan"
                style={{
                  fontFamily: F.display, fontSize: 13, fontWeight: 500,
                  padding: "8px 12px", borderRadius: 2, border: "1px solid #F4F1EB26",
                  background: "transparent", color: "#D6CFC2", cursor: "pointer",
                }}
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
